import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, AlertCircle } from 'lucide-react';

// Worker pdf.js chargé depuis le CDN (version alignée automatiquement)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Affiche TOUTES les pages d'un PDF en défilement vertical (fonctionne aussi sur mobile,
 * contrairement à l'iframe qui n'affiche que la 1re page sur iOS/Android).
 */
export default function PdfAllPagesViewer({ url }) {
    const containerRef = useRef(null);
    const [numPages, setNumPages] = useState(0);
    const [width, setWidth] = useState(800);
    const [status, setStatus] = useState('loading'); // loading | ready | error

    useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                const w = containerRef.current.clientWidth;
                // Marge horizontale : 16px sur mobile, 32px sur desktop, cap à 1000px
                const pad = window.innerWidth < 640 ? 16 : 32;
                if (w > 0) setWidth(Math.min(w - pad, 1000));
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    return (
        <div ref={containerRef} className="w-full h-full overflow-auto flex flex-col items-center py-4 gap-4 px-2 sm:px-4" data-testid="pdf-all-pages-viewer">
            <Document
                file={url}
                onLoadSuccess={({ numPages: n }) => { setNumPages(n); setStatus('ready'); }}
                onLoadError={() => setStatus('error')}
                loading={
                    <div className="flex items-center gap-2 text-slate-500 text-sm py-10">
                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du PDF…
                    </div>
                }
                error={
                    <div className="flex flex-col items-center gap-2 text-rose-500 text-sm py-10 text-center">
                        <AlertCircle className="w-6 h-6" />
                        Impossible d&apos;afficher ce PDF. Utilisez le téléchargement.
                    </div>
                }
                className="flex flex-col items-center gap-3 w-full"
            >
                {status === 'ready' && Array.from({ length: numPages }, (_, i) => (
                    <div key={i} className="shadow-md mb-3 bg-white" data-testid={`pdf-page-${i + 1}`}>
                        <Page
                            pageNumber={i + 1}
                            width={width}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={
                                <div className="flex items-center justify-center bg-slate-50" style={{ width, height: width * 1.3 }}>
                                    <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                                </div>
                            }
                        />
                    </div>
                ))}
            </Document>
            {status === 'ready' && numPages > 0 && (
                <p className="text-[11px] text-slate-400 pb-2">{numPages} page{numPages > 1 ? 's' : ''}</p>
            )}
        </div>
    );
}
